import { useRef, useState } from "react"
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { useSnackbar } from "../../../utils/notificacoes"
import { executarAcoesCardapioIA, interpretarCardapioIA } from "../../../services/cardapio"
import { useCardapioContext } from "./CardapioContext"

const useStyles = makeStyles(theme => ({
    fab: {
        position: "fixed",
        bottom: theme.spacing(4),
        right: theme.spacing(4),
        zIndex: 1300,
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        color: "white",
        fontWeight: "bold",
        fontSize: 13,
        boxShadow: "0 4px 20px rgba(124,58,237,0.5)",
        // Arrastável: evita que o gesto vire scroll no touch e sinaliza o "pegar".
        cursor: "grab",
        touchAction: "none",
        "&:active": {
            cursor: "grabbing",
        },
        "&:hover": {
            background: "linear-gradient(135deg, #5b21b6 0%, #3730a3 100%)",
            transform: "scale(1.05)",
            boxShadow: "0 6px 24px rgba(124,58,237,0.6)",
        },
        transition: "box-shadow 0.2s ease, background 0.2s ease",
    },
    inlineBtn: {
        borderRadius: 25,
        textTransform: "none",
        fontWeight: 600,
        padding: "8px 22px",
        transition: "all 0.2s ease",
        color: "white",
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        boxShadow: "0 4px 15px rgba(124,58,237,0.35)",
        "&:hover": {
            background: "linear-gradient(135deg, #5b21b6 0%, #3730a3 100%)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(124,58,237,0.45)",
        },
    },
    dialogTitle: {
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        color: "white",
        paddingBottom: theme.spacing(2),
    },
    resumoBox: {
        backgroundColor: "#f5f3ff",
        border: "1px solid #ddd6fe",
        borderRadius: 8,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    badge: {
        display: "inline-block",
        backgroundColor: "#7c3aed",
        color: "white",
        borderRadius: 12,
        padding: "3px 12px",
        fontSize: 13,
        fontWeight: "bold",
        marginBottom: theme.spacing(1),
    },
    listaAlteracoes: {
        maxHeight: 320,
        overflowY: "auto",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        marginTop: theme.spacing(1),
    },
    itemAlteracao: {
        padding: "8px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        "&:not(:last-child)": {
            borderBottom: "1px solid #f3f4f6",
        },
    },
    breadcrumb: {
        fontSize: 11,
        color: "#6b7280",
        fontStyle: "italic",
    },
    nomeItem: {
        fontSize: 13,
        fontWeight: 600,
        color: "#111827",
    },
    acaoItem: {
        fontSize: 12,
        color: "#4b5563",
    },
    tipoChip: {
        display: "inline-block",
        fontSize: 10,
        fontWeight: 700,
        borderRadius: 4,
        padding: "1px 6px",
        marginRight: 6,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    botaoSalvar: {
        backgroundColor: "#14A44D",
        color: "white",
        "&:hover": { backgroundColor: "#108a3e" },
    },
    botaoDescartar: {
        color: "#dc2626",
        borderColor: "#dc2626",
        "&:hover": { backgroundColor: "#fef2f2" },
    },
    hint: {
        color: theme.palette.text.secondary,
        fontSize: 12,
        marginBottom: theme.spacing(2),
    },
    avisoCardapioGrande: {
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        backgroundColor: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: 8,
        padding: "8px 12px",
        marginBottom: theme.spacing(2),
        fontSize: 12,
        color: "#92400e",
    },
}))

// Acima disso, mandar o comando sem citar categoria fica lento e caro: o backend precisa
// enviar o cardapio inteiro para a IA em vez de so a fatia relevante.
const PRODUTOS_CARDAPIO_GRANDE = 200

const TIPO_CORES = {
    categoria: { bg: "#ede9fe", color: "#5b21b6", label: "Categoria" },
    produto: { bg: "#e0f2fe", color: "#0369a1", label: "Produto" },
    opcao: { bg: "#fef9c3", color: "#854d0e", label: "Opção" },
}

function acharCategoria(categorias, id, nome) {
    const nl = (nome || "").toLowerCase()
    return (categorias || []).find(c =>
        id != null ? c.id === id : nl && c.nome?.toLowerCase().includes(nl)
    )
}

function acharProduto(categorias, id, nome) {
    const nl = (nome || "").toLowerCase()
    for (const c of categorias || [])
        for (const p of c.produtos || [])
            if (id != null ? p.id === id : nl && p.nome?.toLowerCase().includes(nl))
                return { produto: p, categoria: c }
    return null
}

function acharOpcao(categorias, id, nome) {
    const nl = (nome || "").toLowerCase()
    for (const c of categorias || [])
        for (const p of c.produtos || [])
            for (const g of p.opcoes || [])
                for (const o of g.opcoes || [])
                    if (id != null ? o.id === id : nl && o.nome?.toLowerCase().includes(nl))
                        return { opcao: o, produto: p, categoria: c }
    return null
}

function acharGrupo(categorias, id, nome) {
    const nl = (nome || "").toLowerCase()
    for (const c of categorias || [])
        for (const p of c.produtos || [])
            for (const g of p.opcoes || [])
                if (id != null ? g.id === id : nl && g.nome?.toLowerCase().includes(nl))
                    return { grupo: g, produto: p, categoria: c }
    return null
}

const ROTULOS_CAMPOS = {
    nome: "Nome",
    valor: "Preço",
    codigo: "Código PDV",
    ordemExib: "Ordem",
    descricao: "Descrição",
    ativo: "Disponível",
    permiteObservacao: "Permite observação",
    tipoProducao: "CFOP",
    impressaoAlternada: "Impressora",
    produtoExibicao: "Produto de exibição",
    promocional: "Promocional",
    apenasPrimeiraCompra: "Só 1ª compra",
    pontuarProgramaFidelidade: "Pontua fidelidade",
    pontos: "Pontos",
    quantidadeMinima: "Qtd mínima",
    quantidadeMaxima: "Qtd máxima",
    diaSemana: "Dia",
    turnoProduto: "Turno",
    exibicaoCategoriaEnum: "Exibição",
    modalidadePedido: "Modalidade",
    adicional: "Tipo do grupo",
    considerarMaiorValor: "Considerar maior valor",
    considerarMedia: "Considerar média",
    informativo: "Apenas informativo",
}

function descreverCampos(campos) {
    if (!campos || typeof campos !== "object") return ["Alterar campos"]
    return Object.keys(campos).map(k => {
        const v = campos[k]
        const rot = ROTULOS_CAMPOS[k] || k
        if (typeof v === "boolean") return `${rot}: ${v ? "Sim" : "Não"}`
        if (v === null || v === "") return `${rot}: (vazio)`
        if (k === "valor") return `${rot}: R$ ${Number(v).toFixed(2)}`
        return `${rot}: ${v}`
    })
}

// Converte a lista de acoes da IA em itens legiveis para a previa (antes de aplicar).
function descreverAcoes(acoes, categorias) {
    const out = []
    const preco = v => `R$ ${Number(v || 0).toFixed(2)}`
    for (const a of Array.isArray(acoes) ? acoes : []) {
        const { tipo, id, nome, novoValor: nv } = a
        switch (tipo) {
            case "INATIVAR_PRODUTO":
            case "ATIVAR_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome || "produto",
                    breadcrumb: r?.categoria?.nome,
                    acoes: [tipo === "ATIVAR_PRODUTO" ? "Ativar" : "Inativar"],
                })
                break
            }
            case "INATIVAR_CATEGORIA":
            case "ATIVAR_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                out.push({
                    tipo: "categoria",
                    nome: c?.nome || nome || "categoria",
                    acoes: [tipo === "ATIVAR_CATEGORIA" ? "Ativar" : "Inativar"],
                })
                break
            }
            case "ALTERAR_NOME_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                out.push({ tipo: "categoria", nome: c?.nome || nome, acoes: [`Nome -> "${nv}"`] })
                break
            }
            case "INATIVAR_TODOS_PRODUTOS_CATEGORIA":
            case "ATIVAR_TODOS_PRODUTOS_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                const at = tipo.startsWith("ATIVAR")
                out.push({
                    tipo: "categoria",
                    nome: c?.nome || nome,
                    acoes: [`${at ? "Ativar" : "Inativar"} todos os produtos`],
                })
                break
            }
            case "INATIVAR_TODOS_ADICIONAIS":
            case "ATIVAR_TODOS_ADICIONAIS": {
                const at = tipo.startsWith("ATIVAR")
                out.push({
                    tipo: "opcao",
                    nome: "Todos os adicionais",
                    acoes: [`${at ? "Ativar" : "Inativar"} todas as opcoes`],
                })
                break
            }
            case "ALTERAR_PRECO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: [`Preco -> ${preco(a.valor)}`],
                })
                break
            }
            case "ALTERAR_NOME_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: [`Nome -> "${nv}"`],
                })
                break
            }
            case "ALTERAR_DESCRICAO_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: ["Descricao alterada"],
                })
                break
            }
            case "ALTERAR_CODIGO_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: [`Codigo PDV -> "${nv}"`],
                })
                break
            }
            case "INATIVAR_OPCAO":
            case "ATIVAR_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.opcao?.nome || nome,
                    breadcrumb: r ? `${r.categoria.nome} > ${r.produto.nome}` : undefined,
                    acoes: [tipo === "ATIVAR_OPCAO" ? "Ativar" : "Inativar"],
                })
                break
            }
            case "ALTERAR_NOME_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.opcao?.nome || nome, acoes: [`Nome -> "${nv}"`] })
                break
            }
            case "ALTERAR_CODIGO_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.opcao?.nome || nome, acoes: [`Codigo PDV -> "${nv}"`] })
                break
            }
            case "INCLUIR_PRODUTO": {
                out.push({
                    tipo: "produto",
                    nome: nome || "Novo produto",
                    breadcrumb: a.categoria,
                    acoes: [`Criar produto (${preco(a.valor)})`],
                })
                break
            }
            case "INCLUIR_GRUPO_OPCOES": {
                const r = acharProduto(categorias, id, nome)
                const ops = (a.opcoes || []).map(o => `${o.nome} (${preco(o.valor)})`)
                out.push({
                    tipo: "opcao",
                    nome: nv || "Novo passo de montagem",
                    breadcrumb: r?.produto?.nome
                        ? `Produto: ${r.produto.nome}`
                        : nome
                        ? `Produto: ${nome}`
                        : undefined,
                    acoes: [
                        "Criar passo de montagem",
                        ...(ops.length ? [`Opcoes: ${ops.join(", ")}`] : []),
                    ],
                })
                break
            }
            case "INCLUIR_CATEGORIA": {
                out.push({
                    tipo: "categoria",
                    nome: nome || "Nova categoria",
                    acoes: ["Criar categoria"],
                })
                break
            }
            case "INCLUIR_OPCAO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: nv || "Nova opção",
                    breadcrumb: r?.grupo?.nome
                        ? `Passo: ${r.grupo.nome}`
                        : nome
                        ? `Passo: ${nome}`
                        : undefined,
                    acoes: [`Adicionar ao passo (${preco(a.valor)})`],
                })
                break
            }
            case "INATIVAR_GRUPO":
            case "ATIVAR_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.grupo?.nome || nome || "passo",
                    breadcrumb: r?.produto?.nome ? `Produto: ${r.produto.nome}` : undefined,
                    acoes: [tipo === "ATIVAR_GRUPO" ? "Ativar passo" : "Inativar passo"],
                })
                break
            }
            case "ALTERAR_NOME_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.grupo?.nome || nome, acoes: [`Nome do passo -> "${nv}"`] })
                break
            }
            case "ALTERAR_QUANTIDADES_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                const mn = a.quantidadeMinima != null ? a.quantidadeMinima : "?"
                const mx = a.quantidadeMaxima != null ? a.quantidadeMaxima : "?"
                out.push({ tipo: "opcao", nome: r?.grupo?.nome || nome, acoes: [`Mín/Máx -> ${mn}/${mx}`] })
                break
            }
            case "ALTERAR_ORDEM_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.grupo?.nome || nome, acoes: [`Ordem -> ${a.valor}`] })
                break
            }
            case "ALTERAR_ORDEM_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                out.push({ tipo: "categoria", nome: c?.nome || nome, acoes: [`Ordem -> ${a.valor}`] })
                break
            }
            case "ALTERAR_ORDEM_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: [`Ordem -> ${a.valor}`],
                })
                break
            }
            case "ALTERAR_ORDEM_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.opcao?.nome || nome, acoes: [`Ordem -> ${a.valor}`] })
                break
            }
            case "ALTERAR_PRECO_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.opcao?.nome || nome,
                    breadcrumb: r ? `${r.categoria.nome} > ${r.produto.nome}` : undefined,
                    acoes: [`Preço -> ${preco(a.valor)}`],
                })
                break
            }
            case "ALTERAR_DESCRICAO_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({ tipo: "opcao", nome: r?.opcao?.nome || nome, acoes: ["Descrição alterada"] })
                break
            }
            case "ALTERAR_CATEGORIA": {
                const c = acharCategoria(categorias, id, nome)
                out.push({ tipo: "categoria", nome: c?.nome || nome, acoes: descreverCampos(a.campos) })
                break
            }
            case "ALTERAR_PRODUTO": {
                const r = acharProduto(categorias, id, nome)
                out.push({
                    tipo: "produto",
                    nome: r?.produto?.nome || nome,
                    breadcrumb: r?.categoria?.nome,
                    acoes: descreverCampos(a.campos),
                })
                break
            }
            case "ALTERAR_GRUPO": {
                const r = acharGrupo(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.grupo?.nome || nome,
                    breadcrumb: r?.produto?.nome ? `Produto: ${r.produto.nome}` : undefined,
                    acoes: descreverCampos(a.campos),
                })
                break
            }
            case "ALTERAR_OPCAO": {
                const r = acharOpcao(categorias, id, nome)
                out.push({
                    tipo: "opcao",
                    nome: r?.opcao?.nome || nome,
                    breadcrumb: r ? `${r.categoria.nome} > ${r.produto.nome}` : undefined,
                    acoes: descreverCampos(a.campos),
                })
                break
            }
            default:
                out.push({ tipo: "produto", nome: nome || tipo || "acao", acoes: [tipo || "Acao"] })
        }
    }
    return out
}

export default function FloatingIAButton({ inline = false, style }) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    const { categorias, recarregarCardapio } = useCardapioContext()

    const [open, setOpen] = useState(false)
    const [texto, setTexto] = useState("")
    const [loadingIA, setLoadingIA] = useState(false)
    const [salvando, setSalvando] = useState(false)
    const [resultado, setResultado] = useState(null)

    // Posição do FAB quando arrastado. null = usa o canto padrão (bottom/right do CSS).
    // Persistida para o botão continuar onde o usuário deixou entre recarregamentos.
    const [posicao, setPosicao] = useState(() => {
        try {
            const salvo = localStorage.getItem("posicaoBotaoIA")
            return salvo ? JSON.parse(salvo) : null
        } catch {
            return null
        }
    })
    // Estado do gesto mantido em ref para não re-renderizar a cada movimento do mouse.
    const arrasto = useRef({ ativo: false, moveu: false, sx: 0, sy: 0, dx: 0, dy: 0, w: 0, h: 0 })

    const handlePointerDown = e => {
        const r = e.currentTarget.getBoundingClientRect()
        arrasto.current = {
            ativo: true,
            moveu: false,
            sx: e.clientX,
            sy: e.clientY,
            dx: e.clientX - r.left,
            dy: e.clientY - r.top,
            w: r.width,
            h: r.height,
        }
        e.currentTarget.setPointerCapture?.(e.pointerId)
    }

    const handlePointerMove = e => {
        const a = arrasto.current
        if (!a.ativo) return
        // Só entra em modo "arrastando" depois de passar de um limiar — assim um
        // clique com micro-tremor ainda abre o diálogo em vez de mover o botão.
        if (!a.moveu && Math.hypot(e.clientX - a.sx, e.clientY - a.sy) < 5) return
        a.moveu = true
        const margem = 8
        const x = Math.max(margem, Math.min(e.clientX - a.dx, window.innerWidth - a.w - margem))
        const y = Math.max(margem, Math.min(e.clientY - a.dy, window.innerHeight - a.h - margem))
        setPosicao({ x, y })
    }

    const handlePointerUp = e => {
        const a = arrasto.current
        a.ativo = false
        e.currentTarget.releasePointerCapture?.(e.pointerId)
        if (a.moveu) {
            setPosicao(p => {
                if (p) {
                    try {
                        localStorage.setItem("posicaoBotaoIA", JSON.stringify(p))
                    } catch {
                        /* ignora indisponibilidade do storage */
                    }
                }
                return p
            })
        }
    }

    // Só abre o diálogo se foi um clique de verdade (não o fim de um arrasto).
    const handleClickIA = () => {
        if (arrasto.current.moveu) {
            arrasto.current.moveu = false
            return
        }
        setTexto("")
        setResultado(null)
        setOpen(true)
    }

    const autorizacao = localStorage.getItem("chaveSistema")

    // O cardápio já está em memória, então dá para orientar antes de o usuário digitar —
    // em vez de deixar ele escrever o comando e só então descobrir que ficou lento.
    const totalProdutos = (categorias || []).reduce((n, c) => n + (c.produtos?.length || 0), 0)
    const cardapioGrande = totalProdutos > PRODUTOS_CARDAPIO_GRANDE

    // FASE 1: interpreta o comando e apenas MOSTRA a prévia — nada é aplicado na
    // tela nem persistido até o usuário confirmar no botão "Salvar".
    const handleEnviar = async () => {
        if (!texto.trim()) return
        setLoadingIA(true)
        try {
            const response = await interpretarCardapioIA(texto.trim(), autorizacao)

            if (response.retornoErro) {
                enqueueSnackbar(response.mensagem, { variant: "error" })
                return
            }
            if (response.erro) {
                enqueueSnackbar(response.erro, { variant: "error" })
                return
            }

            const acoes = Array.isArray(response.acoes) ? response.acoes : []
            const alteracoes = descreverAcoes(acoes, categorias)
            setResultado({ acoes, resumo: response.resumo, alteracoes })
        } catch {
            enqueueSnackbar("Erro ao processar a solicitação", { variant: "error" })
        } finally {
            setLoadingIA(false)
        }
    }

    // FASE 2: só aqui as alterações são persistidas e, em caso de sucesso,
    // refletidas na tela.
    const handleSalvarAgora = async () => {
        if (!resultado?.acoes || resultado.acoes.length === 0) {
            handleFechar()
            return
        }
        setSalvando(true)
        try {
            const response = await executarAcoesCardapioIA(resultado.acoes, autorizacao)
            if (response.retornoErro) {
                enqueueSnackbar(response.mensagem, { variant: "error" })
            } else if (response.erro) {
                enqueueSnackbar(response.erro, { variant: "error" })
            } else {
                // Re-busca pela mesma fonte do F5 (formato correto) em vez de usar
                // o CardapioView retornado, que tem shape diferente e nao renderiza.
                await recarregarCardapio()
                const n = response.alteracoesRealizadas ?? 0
                enqueueSnackbar(
                    n > 0
                        ? `${n} ${n === 1 ? "alteração aplicada" : "alterações aplicadas"} pela IA!`
                        : "Nenhuma alteração foi aplicada.",
                    { variant: n > 0 ? "success" : "warning" }
                )
                handleFechar()
            }
        } catch {
            enqueueSnackbar("Erro ao aplicar as alterações", { variant: "error" })
        } finally {
            setSalvando(false)
        }
    }

    // Descarta a prévia. Como nada foi aplicado à tela, basta fechar.
    const handleDescartar = () => {
        handleFechar()
        enqueueSnackbar("Alterações da IA descartadas", { variant: "success" })
    }

    const handleFechar = () => {
        setOpen(false)
        setTexto("")
        setResultado(null)
    }

    return (
        <>
            {inline ? (
                <Button
                    className={classes.inlineBtn}
                    style={style}
                    onClick={() => {
                        setTexto("")
                        setResultado(null)
                        setOpen(true)
                    }}
                >
                    ✦ IA
                </Button>
            ) : (
                <Tooltip
                    title="Arraste para mover · clique para editar o cardápio com IA"
                    placement="left"
                >
                    <Fab
                        className={classes.fab}
                        variant="extended"
                        style={
                            posicao
                                ? { left: posicao.x, top: posicao.y, right: "auto", bottom: "auto" }
                                : undefined
                        }
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onClick={handleClickIA}
                    >
                        ✦ IA
                    </Fab>
                </Tooltip>
            )}

            <Dialog
                open={open}
                onClose={resultado || loadingIA ? undefined : handleFechar}
                maxWidth={resultado ? "md" : "sm"}
                fullWidth
                disableBackdropClick={loadingIA || !!resultado}
            >
                {!resultado ? (
                    <>
                        <DialogTitle className={classes.dialogTitle}>
                            Editar cardápio com Inteligência Artificial
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            <Typography className={classes.hint}>
                                Descreva em linguagem natural o que deseja alterar no cardápio.
                            </Typography>
                            {cardapioGrande && (
                                <div className={classes.avisoCardapioGrande}>
                                    <span>💡</span>
                                    <span>
                                        Seu cardápio é grande ({totalProdutos} produtos).{" "}
                                        <strong>Cite a categoria no comando</strong> — ex.: "Na categoria
                                        Esfihas, ..." — para um resultado melhor e mais rápido.
                                    </span>
                                </div>
                            )}
                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                variant="outlined"
                                label="O que deseja fazer?"
                                placeholder='Ex: "Inative todos os itens da categoria Pizzas"'
                                value={texto}
                                onChange={e => setTexto(e.target.value)}
                                disabled={loadingIA}
                                autoFocus
                                onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handleEnviar() }}
                            />
                            <Typography style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, textAlign: "right" }}>
                                Ctrl + Enter para enviar
                            </Typography>
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px" }}>
                            <Button onClick={handleFechar} disabled={loadingIA}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleEnviar}
                                disabled={loadingIA || !texto.trim()}
                                style={{
                                    background: loadingIA || !texto.trim() ? undefined : "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
                                    color: loadingIA || !texto.trim() ? undefined : "white",
                                    minWidth: 130,
                                }}
                                startIcon={loadingIA ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {loadingIA ? "Processando..." : "Enviar ✦"}
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogTitle className={classes.dialogTitle}>
                            Confira as alterações antes de aplicar
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            <span className={classes.badge}>
                                {resultado.alteracoes?.length || 0}{" "}
                                {(resultado.alteracoes?.length || 0) === 1
                                    ? "alteração"
                                    : "alterações"}{" "}
                                a aplicar
                            </span>

                            <div className={classes.resumoBox}>
                                <Typography variant="body2">{resultado.resumo}</Typography>
                            </div>

                            <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 8 }}>
                                Nada foi alterado ainda. Revise abaixo e clique em{" "}
                                <strong>Aplicar alterações</strong> para confirmar.
                            </Typography>

                            {resultado.alteracoes?.length > 0 && (
                                <>
                                    <Typography variant="caption" style={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        Itens que serão alterados
                                    </Typography>
                                    <div className={classes.listaAlteracoes}>
                                        {resultado.alteracoes.map((alt, i) => {
                                            const cor = TIPO_CORES[alt.tipo]
                                            return (
                                                <div key={i} className={classes.itemAlteracao}>
                                                    <div>
                                                        <span
                                                            className={classes.tipoChip}
                                                            style={{ backgroundColor: cor.bg, color: cor.color }}
                                                        >
                                                            {cor.label}
                                                        </span>
                                                        <span className={classes.nomeItem}>{alt.nome}</span>
                                                    </div>
                                                    {alt.breadcrumb && (
                                                        <span className={classes.breadcrumb}>{alt.breadcrumb}</span>
                                                    )}
                                                    <span className={classes.acaoItem}>
                                                        {alt.acoes.join(" · ")}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </>
                            )}
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px" }}>
                            <Button
                                variant="outlined"
                                className={classes.botaoDescartar}
                                onClick={handleDescartar}
                                disabled={salvando}
                            >
                                Descartar
                            </Button>
                            <Button
                                variant="contained"
                                className={classes.botaoSalvar}
                                onClick={handleSalvarAgora}
                                disabled={salvando}
                                startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {salvando ? "Aplicando..." : "Aplicar alterações"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    )
}
